import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";

/**
 * Cinematic side-by-side rebrand reveal.
 * Stage 1: Old logo enters from left (with BEFORE label)
 * Stage 2: Arrow draws in
 * Stage 3: New wordmark enters from right (with NOW label)
 * Stage 4: onComplete fires → hero content appears below
 */
export default function RebrandReveal({ oldLogo, newLogo, onComplete }) {
  const [stage, setStage] = useState(0);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    const timers = [
      setTimeout(() => setStage(1), 200),   // old in
      setTimeout(() => setStage(2), 1100),  // arrow
      setTimeout(() => setStage(3), 1700),  // new in
      setTimeout(() => {
        setStage(4);
        onCompleteRef.current && onCompleteRef.current();
      }, 2700),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div
      data-testid="rebrand-reveal"
      className="relative w-full flex items-center justify-center md:justify-start"
    >
      <div className="grid grid-cols-[auto_auto_auto] items-center gap-6 md:gap-10">
        {/* OLD */}
        <AnimatePresence>
          {stage >= 1 && (
            <motion.div
              key="old"
              initial={{ opacity: 0, x: -30, scale: 0.92 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-col items-center"
            >
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-400 mb-3 font-sans">
                Before
              </div>
              <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 overflow-hidden shadow-[0_20px_50px_-25px_rgba(234,88,12,0.55)] ring-1 ring-black/5">
                <img
                  src={oldLogo}
                  alt="Self-Employed"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="font-serif text-base sm:text-lg md:text-xl text-zinc-900 mt-4">
                Self-Employed
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ARROW */}
        <div className="flex flex-col items-center min-w-[60px] md:min-w-[100px]">
          <div className="text-[10px] tracking-[0.3em] uppercase text-transparent mb-3 font-sans select-none">
            ·
          </div>
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            animate={stage >= 2 ? { opacity: 1, scaleX: 1 } : {}}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            style={{ transformOrigin: "left center" }}
            className="flex items-center gap-2 text-[#EA580C]"
          >
            <div className="h-px w-8 md:w-16 bg-current" />
            <ArrowRight className="w-5 h-5 md:w-6 md:h-6" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={stage >= 2 ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="font-serif italic text-xs md:text-sm text-zinc-500 mt-3"
          >
            is now
          </motion.div>
        </div>

        {/* NEW */}
        <AnimatePresence>
          {stage >= 3 && (
            <motion.div
              key="new"
              initial={{ opacity: 0, x: 30, scale: 0.96 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-col items-center"
            >
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#EA580C] mb-3 font-sans">
                Now
              </div>
              <div className="w-28 h-20 sm:w-36 sm:h-24 md:w-48 md:h-28 flex items-center justify-center">
                <img
                  src={newLogo}
                  alt="Entrepreneur Stories"
                  data-testid="new-logo"
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <div className="font-serif text-base sm:text-lg md:text-xl text-zinc-900 mt-4">
                Entrepreneur Stories
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
